// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — fenêtres des mandats (Instances et mandats) :
//   • attribuer un mandat : personne (salariés de l'API existante), nature (titulaire, intérim, délégation),
//     origine, dates, pièce justificative OBLIGATOIRE déposée d'abord (POST /socle/pieces), puis enregistrement ;
//     le mandat reste « à confirmer » par une autre personne habilitée (double regard) ;
//   • terminer (motif de fin) ou révoquer (pièce obligatoire) ;
//   • détail d'un mandat (journal, pièces) ; « Qui occupait cette fonction à la date… ».
// Les personnes ne sont jamais inventées : seule une personne existante peut être choisie.
// ═══════════════════════════════════════════════════════════════════════
import { useEffect, useState } from 'react';
import { CalendarSearch, FileText, UserPlus, UserX, Gavel, Search } from 'lucide-react';
import { Badge, Bouton, Squelette, formaterDate } from '../../design/composants';
import {
  useDs, erreurDe, toArray, libelle, aujourdhuiDakar, dateHeure, NATURES_MANDAT, ORIGINES_MANDAT, MOTIFS_FIN, STATUTS_MANDAT, JOURNAL_MANDAT,
  PIECES_MANDAT, POUVOIRS,
} from './api';
import { Fenetre, Champ, PiedFenetre, ErreurServeur, ChoixPiece, pieceComplete, ListePieces, Etat, useLecture } from './commun';

const PIECE_VIDE = { fichier: null, typePiece: 'PV', libelle: '' };

/** Dépôt d'une pièce, une seule fois même si l'acte qui suit échoue (une pièce ne se supprime pas). */
function usePieceUnique(organisationId, sujetType = 'MANDAT', sujetId = null) {
  const ds = useDs();
  const [saisie, setSaisie] = useState(PIECE_VIDE);
  const [deposee, setDeposee] = useState(null);
  const deposer = async () => {
    if (deposee) return deposee;
    const p = await ds.deposerPiece({ fichier: saisie.fichier, organisationId, sujetType, sujetId, typePiece: saisie.typePiece, libelle: saisie.libelle.trim() });
    setDeposee(p);
    return p;
  };
  return { saisie, setSaisie, deposee, deposer, prete: !!deposee || pieceComplete(saisie) };
}

const nomSalarie = (s) => [s.prenom, (s.nom || '').toUpperCase()].filter(Boolean).join(' ');

/** Recherche d'une personne parmi les salariés que l'utilisateur peut voir (API /api/salaries). */
export function ChoixPersonne({ choisie, onChoisir }) {
  const ds = useDs();
  const [q, setQ] = useState('');
  const [resultats, setResultats] = useState(null);
  const [erreur, setErreur] = useState(null);
  useEffect(() => {
    const terme = q.trim();
    if (terme.length < 2) { setResultats(null); return undefined; }
    let vivant = true;
    const t = setTimeout(() => {
      ds.chercherSalaries(terme)
        .then((r) => { if (vivant) { setResultats(toArray(r)); setErreur(null); } })
        .catch((e) => { if (vivant) { setErreur(erreurDe(e, 'Recherche impossible.')); setResultats(null); } });
    }, 300);
    return () => { vivant = false; clearTimeout(t); };
  }, [ds, q]);
  if (choisie) {
    return (
      <Champ libelle="Personne" requis>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
          <strong style={{ fontWeight: 500 }}>{nomSalarie(choisie)}</strong>
          <span style={{ color: 'var(--sg-texte-3)' }}>{choisie.matricule}{choisie.intitule ? ` · ${choisie.intitule}` : ''}</span>
          <Bouton petit variante="lien" onClick={() => onChoisir(null)}>Changer</Bouton>
        </div>
      </Champ>
    );
  }
  return (
    <div style={{ marginBottom: 12 }}>
      <Champ libelle="Personne" requis aide="Nom, prénom ou matricule (2 caractères au moins). Seuls les salariés de votre périmètre apparaissent.">
        <div className="rh-recherche">
          <Search size={16} aria-hidden="true" />
          <input type="search" className="rh-champ-saisie" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une personne" />
        </div>
      </Champ>
      {erreur && <p style={{ margin: '-6px 0 8px', fontSize: 13, color: 'var(--sg-danger)' }}>{erreur.message}</p>}
      {resultats && !resultats.length && <p className="rh-form-aide" style={{ margin: '-6px 0 8px' }}>Aucune personne trouvée.</p>}
      {resultats && resultats.length > 0 && (
        <ul role="listbox" aria-label="Personnes trouvées" style={{ listStyle: 'none', margin: '-4px 0 0', padding: 0, border: '1px solid var(--sg-bord)', borderRadius: 6, maxHeight: 220, overflowY: 'auto' }}>
          {resultats.map((s) => (
            <li key={s.id}>
              <button type="button" role="option" aria-selected="false" className="rh-menu-item" style={{ width: '100%' }} onClick={() => onChoisir(s)}>
                <span>{nomSalarie(s)}</span>
                <span style={{ color: 'var(--sg-texte-3)', marginLeft: 6 }}>{s.matricule}{s.intitule ? ` · ${s.intitule}` : ''}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Attribuer un mandat sur une fonction. `titulaires` : mandats TITULAIRE en cours ou suspendus de la fonction
 * (origine possible d'un intérim ou d'une délégation).
 */
export function FenetreAttribuerMandat({ organisationId, fonction, titulaires, onFermer, onFait }) {
  const ds = useDs();
  const piece = usePieceUnique(organisationId);
  const [personne, setPersonne] = useState(null);
  const [nature, setNature] = useState('TITULAIRE');
  const [origineId, setOrigineId] = useState(titulaires[0]?.id || '');
  const [origine, setOrigine] = useState('ELECTION');
  const [debut, setDebut] = useState(aujourdhuiDakar());
  const [fin, setFin] = useState('');
  const [cles, setCles] = useState([]);
  const [plafond, setPlafond] = useState('');
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [fait, setFait] = useState(null);

  const supplee = nature !== 'TITULAIRE';
  const pouvoirs = toArray(fonction.cles);
  const plafondOk = plafond === '' || /^\d+$/.test(plafond);
  const pret = personne && origine && debut && piece.prete && (!fin || fin >= debut)
    && (!supplee || (origineId && fin)) && (nature !== 'DELEGATION' || (cles.length > 0 && plafondOk));

  const envoyer = async () => {
    setEnCours(true); setErreur(null);
    try {
      const p = await piece.deposer();
      const corps = {
        fonction_id: fonction.id, personne_id: personne.id, nature, origine, date_debut: debut, piece_id: p.id,
        ...(fin ? { date_fin_prevue: fin } : {}),
        ...(supplee ? { mandat_origine_id: origineId } : {}),
        ...(nature === 'DELEGATION' ? { perimetre_delegation: { cles, ...(plafond ? { plafond_montant: Number(plafond) } : {}) } } : {}),
      };
      setFait(await ds.enregistrerMandat(corps));
    } catch (e) { setErreur(erreurDe(e)); }
    setEnCours(false);
  };

  if (fait) {
    return (
      <Fenetre titre="Mandat enregistré" Icone={UserPlus} onFermer={() => onFait(fait)}>
        <p style={{ margin: '0 0 12px', fontSize: 14 }}>
          Le mandat de <strong style={{ fontWeight: 500 }}>{fait.personne}</strong> sur la fonction « {fait.fonction_libelle} » est enregistré.
          Il prendra effet après sa <strong style={{ fontWeight: 500 }}>confirmation par une autre personne habilitée</strong> (ni vous, ni le titulaire).
        </p>
        <PiedFenetre><Bouton variante="primaire" onClick={() => onFait(fait)}>Fermer</Bouton></PiedFenetre>
      </Fenetre>
    );
  }

  return (
    <Fenetre titre={`Attribuer un mandat — ${fonction.libelle}`} Icone={UserPlus} onFermer={onFermer} large>
      <ErreurServeur erreur={erreur} />
      {piece.deposee && erreur && <p className="rh-form-aide" style={{ margin: '-8px 0 12px' }}>La pièce est déjà déposée : corrigez le formulaire, elle sera réutilisée.</p>}
      <ChoixPersonne choisie={personne} onChoisir={setPersonne} />
      <fieldset style={{ border: 0, padding: 0, margin: '0 0 12px' }}>
        <legend style={{ fontSize: 13, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 6 }}>Nature du mandat<span className="rh-form-requis" aria-hidden="true">*</span></legend>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 14 }}>
          {Object.entries(NATURES_MANDAT).map(([c, l]) => {
            const indisponible = c !== 'TITULAIRE' && !titulaires.length;
            return (
              <label key={c} style={{ display: 'inline-flex', gap: 6, alignItems: 'center', color: indisponible ? 'var(--sg-texte-4)' : undefined }}
                title={indisponible ? 'Un intérim ou une délégation suppose un titulaire en fonction.' : undefined}>
                <input type="radio" name="nature" value={c} checked={nature === c} disabled={indisponible} onChange={() => setNature(c)} />{l}
              </label>
            );
          })}
        </div>
        {nature === 'INTERIM' && <p className="rh-form-aide" style={{ margin: '6px 0 0' }}>L'intérimaire supplée le titulaire pendant une période bornée ; le titulaire garde ses pouvoirs.</p>}
        {nature === 'DELEGATION' && <p className="rh-form-aide" style={{ margin: '6px 0 0' }}>La délégation ne transmet que les pouvoirs cochés ; elle tombe avec le mandat du délégant.</p>}
      </fieldset>
      {supplee && titulaires.length > 1 && (
        <Champ libelle="Titulaire suppléé" requis>
          <select className="rh-champ-saisie" value={origineId} onChange={(e) => setOrigineId(e.target.value)}>
            {titulaires.map((t) => <option key={t.id} value={t.id}>{t.personne} (depuis le {formaterDate(t.date_debut)})</option>)}
          </select>
        </Champ>
      )}
      {supplee && titulaires.length === 1 && <p style={{ margin: '0 0 12px', fontSize: 14 }}>Titulaire suppléé : {titulaires[0].personne}.</p>}
      {nature === 'DELEGATION' && (
        <fieldset style={{ border: '1px solid var(--sg-bord)', borderRadius: 6, padding: '10px 12px', margin: '0 0 12px' }}>
          <legend style={{ fontSize: 13, fontWeight: 500, color: 'var(--sg-texte-2)', padding: '0 4px' }}>Pouvoirs délégués<span className="rh-form-requis" aria-hidden="true">*</span></legend>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 6 }}>
            {pouvoirs.map((k) => (
              <label key={k} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
                <input type="checkbox" checked={cles.includes(k)} onChange={() => setCles(cles.includes(k) ? cles.filter((x) => x !== k) : [...cles, k])} />
                {POUVOIRS[k] || k}
              </label>
            ))}
          </div>
          <div style={{ marginTop: 10, maxWidth: 260 }}>
            <Champ libelle="Plafond par acte financier (FCFA)" aide="Facultatif. Entier, sans espace.">
              <input className="rh-champ-saisie" inputMode="numeric" value={plafond} onChange={(e) => setPlafond(e.target.value.replace(/\s/g, ''))} />
            </Champ>
          </div>
        </fieldset>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <Champ libelle="Origine" requis>
          <select className="rh-champ-saisie" value={origine} onChange={(e) => setOrigine(e.target.value)}>
            {Object.entries(ORIGINES_MANDAT).map(([c, l]) => <option key={c} value={c}>{l}</option>)}
          </select>
        </Champ>
        <Champ libelle="Date de début" requis>
          <input type="date" className="rh-champ-saisie" value={debut} onChange={(e) => setDebut(e.target.value)} />
        </Champ>
        <Champ libelle="Fin prévue" requis={supplee} aide="Dernier jour en fonction.">
          <input type="date" className="rh-champ-saisie" value={fin} min={debut || undefined} onChange={(e) => setFin(e.target.value)} />
        </Champ>
      </div>
      <ChoixPiece valeur={piece.saisie} onChange={piece.setSaisie} types={PIECES_MANDAT} deposee={piece.deposee} libelleChamp="Pièce justificative (PV, décision ou statuts)" />
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="primaire" Icone={UserPlus} disabled={!pret || enCours} onClick={envoyer}>{enCours ? 'Enregistrement…' : 'Enregistrer le mandat'}</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

const MOTIFS_TERMINER = ['DEMISSION', 'FIN_INTERIM', 'FIN_CONTRAT', 'DECES', 'AUTRE'];

/** Mettre fin à un mandat (motif) ou le révoquer (pièce obligatoire). */
export function FenetreFinMandat({ mandat, revocation = false, onFermer, onFait }) {
  const ds = useDs();
  const piece = usePieceUnique(mandat.organisation_id, 'MANDAT', mandat.id);
  const jour = aujourdhuiDakar();
  const max = mandat.date_fin_prevue && mandat.date_fin_prevue < jour ? mandat.date_fin_prevue : jour;
  const [motif, setMotif] = useState(mandat.nature === 'INTERIM' ? 'FIN_INTERIM' : 'DEMISSION');
  const [date, setDate] = useState(max);
  const [avecPiece, setAvecPiece] = useState(revocation);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const pieceRequise = revocation || avecPiece;
  const pret = date && date >= mandat.date_debut && date <= max && (!pieceRequise || piece.prete);

  const envoyer = async () => {
    setEnCours(true); setErreur(null);
    try {
      const p = pieceRequise ? await piece.deposer() : null;
      const r = revocation
        ? await ds.revoquerMandat(mandat.id, { piece_fin_id: p.id, date_fin_effective: date })
        : await ds.terminerMandat(mandat.id, { motif_fin: motif, date_fin_effective: date, ...(p ? { piece_fin_id: p.id } : {}) });
      onFait(r);
    } catch (e) { setErreur(erreurDe(e)); setEnCours(false); }
  };

  return (
    <Fenetre titre={revocation ? 'Révoquer le mandat' : 'Mettre fin au mandat'} Icone={revocation ? Gavel : UserX} onFermer={onFermer}>
      <ErreurServeur erreur={erreur} />
      <p style={{ margin: '0 0 12px', fontSize: 14 }}>
        {mandat.personne} — {mandat.fonction_libelle}{mandat.nature !== 'TITULAIRE' ? ` (${libelle(NATURES_MANDAT, mandat.nature).toLowerCase()})` : ''}, depuis le {formaterDate(mandat.date_debut)}.
      </p>
      {!revocation && (
        <Champ libelle="Motif de fin" requis>
          <select className="rh-champ-saisie" value={motif} onChange={(e) => setMotif(e.target.value)}>
            {MOTIFS_TERMINER.map((c) => <option key={c} value={c}>{libelle(MOTIFS_FIN, c)}</option>)}
          </select>
        </Champ>
      )}
      <Champ libelle="Dernier jour en fonction" requis aide={`Entre le ${formaterDate(mandat.date_debut)} et le ${formaterDate(max)}.`}>
        <input type="date" className="rh-champ-saisie" value={date} min={mandat.date_debut} max={max} onChange={(e) => setDate(e.target.value)} />
      </Champ>
      {!revocation && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginBottom: 12 }}>
          <input type="checkbox" checked={avecPiece} onChange={(e) => setAvecPiece(e.target.checked)} />Joindre une pièce (lettre de démission, PV…)
        </label>
      )}
      {pieceRequise && (
        <ChoixPiece valeur={piece.saisie} onChange={piece.setSaisie} deposee={piece.deposee}
          types={revocation ? PIECES_MANDAT : [...PIECES_MANDAT, 'JUSTIFICATIF', 'AUTRE']}
          libelleChamp={revocation ? 'Pièce de révocation (PV ou décision)' : 'Pièce de fin'} />
      )}
      <PiedFenetre>
        <Bouton onClick={onFermer}>Retour</Bouton>
        <Bouton variante="danger" Icone={revocation ? Gavel : UserX} disabled={!pret || enCours} onClick={envoyer}>
          {revocation ? 'Révoquer' : 'Mettre fin au mandat'}
        </Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

/** Détail d'un mandat : informations, journal, pièces. */
export function FenetreDetailMandat({ mandatId, onFermer }) {
  const ds = useDs();
  const { donnees: m, erreur } = useLecture(() => ds.mandat(mandatId), [ds, mandatId]);
  const pieces = m ? [
    m.piece_id && { id: m.piece_id, contexte: 'Pièce du mandat' },
    m.piece_fin_id && { id: m.piece_fin_id, contexte: 'Pièce de fin' },
    ...toArray(m.journal).filter((j) => j.piece_id).map((j) => ({ id: j.piece_id, contexte: libelle(JOURNAL_MANDAT, j.evenement) })),
  ].filter(Boolean) : [];
  return (
    <Fenetre titre="Détail du mandat" Icone={FileText} onFermer={onFermer} large>
      <ErreurServeur erreur={erreur} />
      {!m && !erreur && <Squelette largeur="60%" />}
      {m && (
        <>
          <div className="rh-grille" style={{ marginBottom: 16 }}>
            <div className="rh-champ"><div className="rh-champ-libelle">Personne</div><div className="rh-champ-valeur">{m.personne}</div></div>
            <div className="rh-champ"><div className="rh-champ-libelle">Fonction</div><div className="rh-champ-valeur">{m.fonction_libelle} · {m.organe_libelle}</div></div>
            <div className="rh-champ"><div className="rh-champ-libelle">Nature</div><div className="rh-champ-valeur">{libelle(NATURES_MANDAT, m.nature)}</div></div>
            <div className="rh-champ"><div className="rh-champ-libelle">Statut</div><div className="rh-champ-valeur"><Etat table={STATUTS_MANDAT} code={m.statut} /></div></div>
            <div className="rh-champ"><div className="rh-champ-libelle">Origine</div><div className="rh-champ-valeur">{libelle(ORIGINES_MANDAT, m.origine)}</div></div>
            <div className="rh-champ"><div className="rh-champ-libelle">Période</div><div className="rh-champ-valeur">
              Du {formaterDate(m.date_debut)}{m.date_fin_effective ? ` au ${formaterDate(m.date_fin_effective)}` : m.date_fin_prevue ? ` au ${formaterDate(m.date_fin_prevue)} (prévue)` : ', sans fin prévue'}
            </div></div>
            {m.motif_fin && <div className="rh-champ"><div className="rh-champ-libelle">Motif de fin</div><div className="rh-champ-valeur">{libelle(MOTIFS_FIN, m.motif_fin)}</div></div>}
            {m.motif_suspension && m.statut === 'SUSPENDU' && <div className="rh-champ"><div className="rh-champ-libelle">Motif de suspension</div><div className="rh-champ-valeur">{m.motif_suspension}</div></div>}
            {m.motif_annulation && <div className="rh-champ"><div className="rh-champ-libelle">Motif d'annulation</div><div className="rh-champ-valeur">{m.motif_annulation}</div></div>}
          </div>
          {m.perimetre_delegation && (
            <p style={{ margin: '0 0 16px', fontSize: 14 }}>
              Pouvoirs délégués : {toArray(m.perimetre_delegation.cles).map((k) => POUVOIRS[k] || k).join(', ')}
              {m.perimetre_delegation.plafond_montant != null ? ` · plafond ${Number(m.perimetre_delegation.plafond_montant).toLocaleString('fr-FR')} FCFA` : ''}
            </p>
          )}
          <h3 className="rh-form-section-titre" style={{ marginTop: 0 }}>Journal</h3>
          <div className="rh-conteneur-tableau" style={{ marginBottom: 16 }}>
            <table className="rh-tableau">
              <thead><tr><th>Date</th><th>Événement</th><th>Motif</th></tr></thead>
              <tbody>
                {toArray(m.journal).map((j) => (
                  <tr key={`${j.evenement}-${j.le}`}><td style={{ whiteSpace: 'nowrap' }}>{dateHeure(j.le)}</td><td>{libelle(JOURNAL_MANDAT, j.evenement)}</td><td>{j.motif ? libelle(MOTIFS_FIN, j.motif) : ''}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <h3 className="rh-form-section-titre">Pièces</h3>
          <ListePieces pieces={pieces} />
        </>
      )}
      <PiedFenetre><Bouton onClick={onFermer}>Fermer</Bouton></PiedFenetre>
    </Fenetre>
  );
}

/** « Qui occupait cette fonction à la date… » (GET /gouvernance/fonctions/:id/titulaire?date=). */
export function FenetreTitulaireA({ fonction, onFermer }) {
  const ds = useDs();
  const [date, setDate] = useState(aujourdhuiDakar());
  const [resultat, setResultat] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const chercher = async () => {
    setEnCours(true); setErreur(null); setResultat(null);
    try { setResultat(await ds.titulaireA(fonction.id, date)); } catch (e) { setErreur(erreurDe(e)); }
    setEnCours(false);
  };
  return (
    <Fenetre titre={`Qui occupait la fonction « ${fonction.libelle} » ?`} Icone={CalendarSearch} onFermer={onFermer}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ flex: '1 1 180px' }}>
          <Champ libelle="À la date du" requis>
            <input type="date" className="rh-champ-saisie" value={date} onChange={(e) => setDate(e.target.value)} />
          </Champ>
        </div>
        <div style={{ marginBottom: 12 }}><Bouton variante="primaire" Icone={Search} disabled={!date || enCours} onClick={chercher}>Rechercher</Bouton></div>
      </div>
      <ErreurServeur erreur={erreur} />
      {resultat && resultat.vacante && (
        <p style={{ margin: '0 0 12px', fontSize: 14 }}><Badge ton="alerte" point>Fonction à pourvoir</Badge> Personne n'occupait cette fonction le {formaterDate(resultat.date, true)}.</p>
      )}
      {resultat && !resultat.vacante && (
        <ul style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: 14 }}>
          {toArray(resultat.occupants).map((o) => (
            <li key={o.mandat_id}>
              <strong style={{ fontWeight: 500 }}>{o.personne}</strong>
              {o.nature !== 'TITULAIRE' ? ` — ${libelle(NATURES_MANDAT, o.nature).toLowerCase()}` : ''}
              {' '}du {formaterDate(o.date_debut)}{o.date_fin ? ` au ${formaterDate(o.date_fin)}` : ''}
              {o.statut === 'SUSPENDU' ? ' (suspendu)' : ''}
            </li>
          ))}
        </ul>
      )}
      <PiedFenetre><Bouton onClick={onFermer}>Fermer</Bouton></PiedFenetre>
    </Fenetre>
  );
}
